import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  PromoPopAudience,
  PromoPopFrequency,
  PromoPopPageKey,
} from "@shared/promoPopPages";
import PromoPopModal, { type PromoPopModalData } from "@/components/promo-pop/PromoPopModal";
import { useAuth } from "@/hooks/useAuth";
import trpc from "@/shared/utils/trpc";

type PublicPromoPop = {
  id: string;
  imageUrl: string;
  linkUrl: string | null;
  buttonLabel: string;
  audience: PromoPopAudience;
  frequency: PromoPopFrequency;
};

type HostPromo = PromoPopModalData & { frequency: PromoPopFrequency };

const DAY_MS = 24 * 60 * 60 * 1000;

function rotateKey(pageKey: string) {
  return `promoPopRotate:${pageKey}`;
}

function sessionSeenKey(id: string) {
  return `promoPopSeen:session:${id}`;
}

function daySeenKey(id: string) {
  return `promoPopSeen:day:${id}`;
}

function everSeenKey(id: string) {
  return `promoPopSeen:ever:${id}`;
}

function matchesAudience(audience: PromoPopAudience, isAuthenticated: boolean): boolean {
  if (audience === "BOTH") return true;
  if (audience === "GUESTS") return !isAuthenticated;
  return isAuthenticated;
}

function isSuppressedByFrequency(promo: PublicPromoPop): boolean {
  const { id, frequency } = promo;
  if (frequency === "EVERY_VISIT") return false;

  try {
    if (frequency === "ONCE_PER_SESSION") {
      return sessionStorage.getItem(sessionSeenKey(id)) === "1";
    }
    if (frequency === "ONCE_PER_DAY") {
      const raw = localStorage.getItem(daySeenKey(id));
      if (!raw) return false;
      const ts = Number(raw);
      if (!Number.isFinite(ts)) return false;
      return Date.now() - ts < DAY_MS;
    }
    if (frequency === "ONCE_EVER") {
      return localStorage.getItem(everSeenKey(id)) === "1";
    }
  } catch {
    /* private mode / quota */
  }
  return false;
}

function markOpenedForFrequency(promo: HostPromo): void {
  const { id, frequency } = promo;
  try {
    if (frequency === "ONCE_PER_SESSION") {
      sessionStorage.setItem(sessionSeenKey(id), "1");
    } else if (frequency === "ONCE_PER_DAY") {
      localStorage.setItem(daySeenKey(id), String(Date.now()));
    }
  } catch {
    /* ignore */
  }
}

function markDismissedForFrequency(promo: HostPromo): void {
  if (promo.frequency !== "ONCE_EVER") return;
  try {
    localStorage.setItem(everSeenKey(promo.id), "1");
  } catch {
    /* ignore */
  }
}

function pickRotatedPromo(pageKey: string, promos: PublicPromoPop[]): PublicPromoPop {
  if (promos.length === 1) return promos[0];

  let index = 0;
  try {
    const raw = sessionStorage.getItem(rotateKey(pageKey));
    const parsed = raw ? Number(raw) : 0;
    index = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
  } catch {
    index = 0;
  }

  const selected = promos[index % promos.length];
  try {
    sessionStorage.setItem(rotateKey(pageKey), String((index + 1) % promos.length));
  } catch {
    /* ignore quota / private mode */
  }
  return selected;
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Loads active promo pops for a page and auto-opens a rotating popup on mount.
 * Filters by audience + frequency (session / day / ever storage).
 */
export default function PromoPopHost({ pageKey }: { pageKey: PromoPopPageKey }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [opened, setOpened] = useState(false);
  const [promo, setPromo] = useState<HostPromo | null>(null);

  useEffect(() => {
    let cancelled = false;

    setOpened(false);
    setPromo(null);

    trpc.promoPop.getForPage
      .query({ pageKey })
      .then((res) => {
        if (cancelled) return;
        const promos = (res.promos ?? []) as PublicPromoPop[];
        const eligible = promos.filter(
          (p) => matchesAudience(p.audience, isAuthenticated) && !isSuppressedByFrequency(p),
        );
        if (!eligible.length) return;

        const selected = pickRotatedPromo(pageKey, eligible);
        const next: HostPromo = {
          id: selected.id,
          imageUrl: selected.imageUrl,
          linkUrl: selected.linkUrl,
          buttonLabel: selected.buttonLabel?.trim() || "Open",
          frequency: selected.frequency,
        };
        setPromo(next);
        setOpened(true);
        markOpenedForFrequency(next);
      })
      .catch(() => {
        /* non-blocking promo */
      });

    return () => {
      cancelled = true;
    };
  }, [pageKey, isAuthenticated]);

  const handleClose = () => {
    if (promo) markDismissedForFrequency(promo);
    setOpened(false);
  };

  const handleCta = () => {
    if (!promo) return;
    markDismissedForFrequency(promo);
    const href = promo.linkUrl?.trim();
    setOpened(false);
    if (!href) return;

    if (isExternalUrl(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(href);
  };

  return (
    <PromoPopModal opened={opened} promo={promo} onClose={handleClose} onCta={handleCta} />
  );
}
